/* eslint-disable react/prop-types */
import React from 'react';
import PropTypes from 'prop-types';
import { Grid, GridItem, Box, Flex, Text, Spinner } from '@chakra-ui/react';
import {
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  Bar,
  BarChart,
} from 'recharts';
import { DailyProfitabilityBarColors } from 'utils/StaticData';
import { formatCurrency } from 'utils/CommonFunction';

const getUniqueRegions = (data) => {
  if (!data || data.length === 0) return [];

  const regions = new Set();
  data.forEach((item) => {
    Object.keys(item).forEach((key) => {
      if (key !== 'time') {
        regions.add(key);
      }
    });
  });

  return Array.from(regions);
};

const DailyProfitabilityGraph = ({ dailyData, isLoading, currency }) => {
  const regions = getUniqueRegions(dailyData?.data);
  const transformedData = dailyData?.data?.map((item) => {
    const date = new Date(item.time);
    return {
      time: `${date.getDate()}/${date.getMonth() + 1}`,
      ...item,
    };
  });

  const CustomBarShape = (props) => {
    const { fill, x, y, width, height, dataKey, payload } = props;
    const activeRegions = Object.keys(payload).filter(
      (key) => key !== 'time' && payload[key] > 0,
    );

    const isTopVisibleBar = activeRegions[activeRegions.length - 1] === dataKey;

    if (height > 0) {
      const radius = 5;

      if (isTopVisibleBar) {
        return (
          <path
            d={`
            M${x},${y + radius}
            Q${x},${y} ${x + radius},${y}
            L${x + width - radius},${y}
            Q${x + width},${y} ${x + width},${y + radius}
            L${x + width},${y + height}
            L${x},${y + height}
            Z
          `}
            fill={fill}
          />
        );
      } else {
        return <rect x={x} y={y} width={width} height={height} fill={fill} />;
      }
    }
    return <path />;
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const time = data?.time;
      return (
        <div
          className="custom-tooltip"
          style={{
            background: '#fff',
            padding: '10px',
            border: '1px solid #ccc',
          }}
        >
          {Object.entries(data)
            ?.filter(([key]) => key !== 'time')
            ?.map(([key, value]) => (
              <>
                <Flex alignItems="center">
                  <Box
                    w="10px"
                    h="10px"
                    bg={DailyProfitabilityBarColors[key]}
                    borderRadius="50%"
                    mr="2"
                  />
                  {time} : {formatCurrency(value, currency)}
                </Flex>
              </>
            ))}
        </div>
      );
    }
    return null;
  };

  return (
    <Grid
      bg="white"
      p={{ base: '4', md: '5' }}
      templateColumns="repeat(12, 1fr)"
      gap={{ base: '16px', lg: '24px' }}
      boxShadow="0px 0px 20px rgba(32, 57, 70, 0.06)"
      borderRadius="20px"
      border="1px solid #D9D9D9"
      w="full"
    >
      <GridItem colSpan={{ base: '12', lg: '12' }}>
        <Box w="full" h="full" bg="white">
          <Flex justify="space-between" align="baseline" mb={4}>
            <Text
              fontSize={{ base: '16px', md: '24px' }}
              fontWeight="semibold"
              color="primary"
            >
              {dailyData?.title}
            </Text>
            {!isLoading && regions.length > 0 && (
              <Flex flexWrap="wrap" align="left" flexDirection="row" gap="20px">
                {regions?.map((regionId) => (
                  <Flex key={regionId} align="center">
                    <Box
                      w="10px"
                      h="10px"
                      bg={DailyProfitabilityBarColors[regionId] || '#cccccc'}
                      borderRadius="50%"
                      mr="2"
                    />
                    <Text fontSize="12px">
                      {regionId === '1'
                        ? 'Netherlands'
                        : regionId === '2'
                          ? 'Spain'
                          : `Region ${regionId}`}
                    </Text>
                  </Flex>
                ))}
              </Flex>
            )}
          </Flex>

          {isLoading ? (
            <Flex justify="center" alignItems="center" height="300px">
              <Spinner size="lg" color="black.500" />
            </Flex>
          ) : (
            <>
              {dailyData?.data?.length > 0 ? (
                <Box overflowX="auto" className="custom-scrollbar">
                  <Box
                    minWidth={`${Math.max(dailyData?.data?.length * 50, 600)}px`}
                  >
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart
                        data={transformedData}
                        margin={{
                          top: 10,
                          right: 30,
                          left: 20,
                          bottom: transformedData?.length > 7 ? 50 : 10,
                        }}
                        barCategoryGap={
                          dailyData?.data?.length < 5 ? '10%' : '20%'
                        }
                      >
                        <CartesianGrid
                          strokeDasharray="3 3"
                          horizontal={true}
                          vertical={false}
                        />
                        <XAxis
                          dataKey="time"
                          interval={0}
                          tick={({ x, y, payload }) => {
                            const fontSize = 12;
                            const angle = dailyData?.data?.length > 7 ? -45 : 0;

                            let truncatedName = payload.value;

                            const maxLength = 15;

                            truncatedName =
                              truncatedName.length > maxLength
                                ? `${truncatedName.substring(0, maxLength)}…`
                                : truncatedName;

                            return (
                              <g transform={`translate(${x},${y})`}>
                                <text
                                  x={0}
                                  y={0}
                                  dy={16}
                                  textAnchor={angle === 0 ? 'middle' : 'end'}
                                  fill="#666"
                                  transform={
                                    angle !== 0 ? `rotate(${angle})` : ''
                                  }
                                  fontSize={fontSize}
                                  fontWeight="600"
                                >
                                  {truncatedName}
                                </text>
                              </g>
                            );
                          }}
                        />
                        <YAxis
                          allowDecimals={false}
                          axisLine={false}
                          tickLine={false}
                          tickFormatter={(value) =>
                            formatCurrency(value, currency)
                          }
                        />

                        <Tooltip content={<CustomTooltip />} />

                        {regions.map((regionId) => (
                          <Bar
                            key={regionId}
                            dataKey={regionId}
                            name={
                              regionId === '1'
                                ? 'Netherlands'
                                : regionId === '2'
                                  ? 'Spain'
                                  : `Region ${regionId}`
                            }
                            fill={
                              DailyProfitabilityBarColors[regionId] || '#cccccc'
                            }
                            stackId="a"
                            shape={<CustomBarShape />}
                          />
                        ))}
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                </Box>
              ) : (
                <Flex justify="center" alignItems="center" height="300px">
                  <Text fontSize="18px" fontWeight="bold" color="gray.500">
                    No records available
                  </Text>
                </Flex>
              )}
            </>
          )}
        </Box>
      </GridItem>
    </Grid>
  );
};

DailyProfitabilityGraph.propTypes = {
  dailyData: PropTypes.shape({
    title: PropTypes.string,
    data: PropTypes.arrayOf(PropTypes.object),
  }),
  isLoading: PropTypes.bool,
};

export default DailyProfitabilityGraph;
